import React, { useEffect, useState } from 'react';
import { Menu, MenuItem, Divider, Typography } from '@mui/material';
import '../styled/pages/dashboard.scss';
import './cwHeader.css';
import { getIcon } from '@ohif/ui';
import { useNavigate, useLocation } from 'react-router-dom';
import { capitalizeFirstLetterOfEachWord, getInitials, sideMenuBasedOnRole } from '../Utils/utils';
import { AppDispatch, RootState } from '../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getProfileDetails } from '../rtk-apis/profile/getProfileDetails';
import { logoutAndWipeBrowser, getHospitalName, getRole } from '../Utils/authUtils';
import SideMenu from './SideMenu';
import { multipleHospitalsApi } from '../rtk-apis/auth/multipleHospitals';
import ChangeHospitalModal from './ChangeHospitalModal';
import Loader from './Loader';
import NotificationBell from './NotificationBell';

const MENU_ITEM_SX = {
  fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
  fontSize: 13.5,
  fontWeight: 500,
  color: '#9fb2cc',
  borderRadius: '9px',
  mx: 0.75,
  my: '2px',
  px: 1.25,
  py: 1,
  minHeight: 'auto',
  '&:hover': { backgroundColor: 'rgba(45,212,191,.1)', color: '#2dd4bf' },
};

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const multipleHospitalsResponse = useSelector(
    (state: RootState) => state.multipleHospitalsResponse
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);
  const go = (path: string) => {
    closeMenu();
    navigate(path);
  };
  const [isChangeHospitalModalOpen, setIsChangeHospitalModalOpen] = useState(false);
  const [sideMenuItems, setSideMenuItems] = useState([]);
  const [userRole, setUserRole] = useState<string>('');
  const role: any = getRole();
  const userEmail = sessionStorage.getItem('email') || localStorage.getItem('email');
  const userName = sessionStorage.getItem('name') || localStorage.getItem('name');
  const hospitalName =
    sessionStorage.getItem('hospitalName') || localStorage.getItem('hospitalName');

  useEffect(() => {
    const role = getRole();
    setUserRole(role);
    setSideMenuItems(sideMenuBasedOnRole(role));
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const eligibleRoles = [
      'Radiologist',
      'Cardiologist',
      'Sonographer',
      'Admin',
      'Practice Manager',
      'Medical Receptionist',
    ];

    if (token && eligibleRoles.includes(role)) {
      if (
        !multipleHospitalsResponse?.hospitals ||
        multipleHospitalsResponse.hospitals.length === 0
      ) {
        dispatch(multipleHospitalsApi({ email: userEmail.toLowerCase() }));
      }
    }
  }, [dispatch]);

  const Logout = () => {
    void logoutAndWipeBrowser(dispatch);
  };

  const toggleSidebar = () => {
    const sidebar = document.getElementsByTagName('body')[0];
    sidebar.classList.toggle('sidebar-hidden');
  };

  const closeSideBar = () => {
    const sidebar = document.getElementsByTagName('body')[0];
    sidebar.classList.remove('sidebar-hidden');
  };

  const handleOpenChangeHospitalModal = () => {
    setIsChangeHospitalModalOpen(true);
    closeMenu();
  };

  const handleCloseChangeHospitalModal = () => {
    setIsChangeHospitalModalOpen(false);
    dispatch(multipleHospitalsApi({ email: userEmail.toLowerCase() }));
  };

  return (
    <div className="page__head cw-head">
      <div className="page__head__logo">
        <div
          className="page__head__logo__img cursor-pointer"
          onClick={() => {
            if (sideMenuItems.length > 0) {
              navigate(sideMenuItems[0].path);
            }
          }}
        >
          {getIcon('logoWeb10')}
        </div>
      </div>

      <div className="page__head__actions">
        <SideMenu
          isOpen={true}
          closeSideBar={closeSideBar}
        />
        <NotificationBell />
        <div className="userProfile">
          <button
            className="userProfile__link"
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            <span className="picIcon">
              <div className="profile">
                <div className="profile__initials">{getInitials(userName)}</div>
              </div>
            </span>
            <span className="Userinfo">
              {getRole() === 'Admin' && (
                <p className="Userinfo__name">{capitalizeFirstLetterOfEachWord(userName)}</p>
              )}
              <p className="Userinfo__role">{role === 'Admin' ? role : ''}</p>
              <p
                className="Userinfo__name"
                title={
                  getRole() === 'Admin'
                    ? getHospitalName() || ''
                    : capitalizeFirstLetterOfEachWord(userName)
                }
              >
                {getRole() === 'Admin' && getRole() !== 'SUPER_ADMIN'
                  ? (() => {
                      // Clinic name shown exactly as entered in the clinic profile
                      // (no title-casing, no "Hospital" suffix) — client ask.
                      const hospitalName = getHospitalName();
                      if (!hospitalName) {
                        return '';
                      }
                      const words = hospitalName.split(' ');
                      return words.length > 4 ? `${words.slice(0, 4).join(' ')}...` : hospitalName;
                    })()
                  : capitalizeFirstLetterOfEachWord(userName)}
              </p>
              <p className="Userinfo__role">{role !== 'Admin' ? role : ''}</p>
              {getRole() !== 'Admin' &&
                getRole() !== 'SUPER_ADMIN' &&
                hospitalName != null &&
                hospitalName !== '' &&
                (() => {
                  // Clinic name shown exactly as stored (no title-casing / "Hospital" suffix)
                  const words = hospitalName.split(' ');
                  const displayName =
                    words.length > 4 ? `${words.slice(0, 4).join(' ')}...` : hospitalName;

                  return (
                    <p
                      className="Userinfo__hospital__name"
                      title={hospitalName}
                    >
                      {displayName}
                    </p>
                  );
                })()}
            </span>
          </button>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 232,
                  backgroundColor: '#16263b',
                  border: '1px solid #314a6b',
                  borderRadius: '14px',
                  boxShadow: '0 22px 54px -18px rgba(0,0,0,0.9)',
                  py: 0.75,
                },
              },
            }}
          >
            <MenuItem
              sx={MENU_ITEM_SX}
              onClick={() => go('/dashboard')}
            >
              Dashboard
            </MenuItem>
            {(role === 'Admin' || role === 'SUPER_ADMIN') && (
              <MenuItem
                sx={MENU_ITEM_SX}
                onClick={() => go('/auditLogs')}
              >
                Audit Logs
              </MenuItem>
            )}
            {role === 'Admin' && (
              <MenuItem
                sx={MENU_ITEM_SX}
                onClick={() => go('/pacs')}
              >
                PACS
              </MenuItem>
            )}
            {role === 'Admin' && (
              <MenuItem
                sx={MENU_ITEM_SX}
                onClick={() => go('/reportTemplate')}
              >
                Report Template
              </MenuItem>
            )}
            {['Admin', 'Sonographer', 'Cardiologist', 'Radiologist', 'Clinician'].includes(
              role
            ) && (
              <MenuItem
                sx={MENU_ITEM_SX}
                onClick={() => go('/dropdownSettings')}
              >
                Report Dropdowns
              </MenuItem>
            )}
            {role === 'Admin' && (
              <MenuItem
                sx={MENU_ITEM_SX}
                onClick={() => go('/clinicProfile')}
              >
                Clinic Profile
              </MenuItem>
            )}
            <MenuItem
              sx={MENU_ITEM_SX}
              onClick={() => go('/myProfile')}
            >
              My Profile
            </MenuItem>
            {multipleHospitalsResponse?.hospitals?.length > 0 &&
              [
                'Radiologist',
                'Cardiologist',
                'Sonographer',
                'Admin',
                'Practice Manager',
                'Medical Receptionist',
              ].includes(role) && (
                <MenuItem
                  sx={MENU_ITEM_SX}
                  onClick={() => {
                    closeMenu();
                    handleOpenChangeHospitalModal();
                  }}
                >
                  Switch Clinic
                </MenuItem>
              )}
            <MenuItem
              sx={MENU_ITEM_SX}
              onClick={() => go('/changePassword')}
            >
              Change Password
            </MenuItem>
            <Typography sx={{ px: 2.25, py: 0.75, fontSize: 11.5, color: '#5b6b84' }}>
              version 0.0.10
            </Typography>
            <Divider sx={{ borderColor: '#243652', my: 0.5 }} />
            <MenuItem
              onClick={() => {
                closeMenu();
                Logout();
              }}
              sx={{
                ...MENU_ITEM_SX,
                color: '#fb7185',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'rgba(251,113,133,.12)', color: '#fb7185' },
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </div>
        <button
          className="btnCollapse btnHamburger"
          id="sidebarCollapse"
          type="button"
          onClick={toggleSidebar}
        >
          {getIcon('hamburgerIcon')}
          <svg
            className="btnCollapse__closeIcon"
            width="23"
            height="23"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13M1 1L13 13"
              stroke="#ffffff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <ChangeHospitalModal
        data={multipleHospitalsResponse?.hospitals}
        isOpen={isChangeHospitalModalOpen}
        handleCloseModal={handleCloseChangeHospitalModal}
      />
      {/* Only spin while the hospital-switch modal is open — not on the background
          hospitals fetch that runs on every page load (that showed a stray top spinner). */}
      <Loader isLoading={isChangeHospitalModalOpen && multipleHospitalsResponse?.loading} />
    </div>
  );
};

export default Header;
